import { Box, Typography } from '@mui/material';
import React, { useRef } from 'react';
import { useIntersectionObserver } from '../../../utilities/customHooks/useIntersectionObserver';
import { Notification } from '../types';
import NotificationItem from './NotificationItem';
import NotificationSkeleton from './Skeletons/NotificationSkeleton';
import SpriteIcon from '../../../core-components/assets/Icon';
import ErrorScreen from '../../../core-components/theme/components/ErrorScreen';

const listContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
};

const loadingContainerStyles = {
    p: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
};

const skeletonItemStyles = {
    p: 2,
    borderBottom: '1px solid #e0e0e0',
    display: 'flex',
    gap: 2,
    width: '100%',
};

const errorContainerStyles = {
    p: 4,
    textAlign: 'center',
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};

const emptyContainerStyles = {
    p: 4,
    textAlign: 'center',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
};

const bottomIndicatorStyles = {
    p: 2,
    textAlign: 'center',
};

const endMessageStyles = {
    textAlign: 'center',
    p: 2,
};

interface NotificationListProps {
    notifications: Notification[];
    isLoading: boolean;
    isError: boolean;
    error?: Error;
    isFetchingNextPage: boolean;
    hasNextPage: boolean;
    fetchNextPage: () => void;
    rootRef: React.RefObject<HTMLElement>;
    handleNotificationStatus: any
}

const NotificationList: React.FC<NotificationListProps> = ({
    notifications,
    isLoading,
    isError,
    error,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    rootRef,
    handleNotificationStatus
}) => {
    const sentinelRef = useRef<HTMLDivElement>(null);
    // const { mutate: markNotificationReadMutation } = useMarkNotificationRead(filter);
    useIntersectionObserver(
         rootRef,
         sentinelRef,
         () => {
            if (hasNextPage && !isFetchingNextPage) {
                fetchNextPage();
            }
        },
        hasNextPage,
        isLoading
    );

    if (isLoading) {
        return (
            <Box sx={listContainerStyles}>
                <Box sx={loadingContainerStyles}>
                    {Array.from({ length: 3 }).map((_, index) => (
                        <Box key={index} sx={skeletonItemStyles}>
                            <NotificationSkeleton />
                        </Box>
                    ))}
                </Box>
            </Box>
        );
    }

    if (isError) {
        return (
            <Box sx={listContainerStyles}>
                <Box sx={errorContainerStyles}>
                    <ErrorScreen icon={<SpriteIcon id='no_data_error' width="201" height="201"/>} message={`Failed to load notifications: ${error?.message || 'Unknown error'}`} />
                </Box>
            </Box>
        );
    }

    if (notifications.length === 0) {
        return (
            <Box sx={listContainerStyles}>
                <Box sx={emptyContainerStyles}>
                    <SpriteIcon id='no_notification' fill='none' height='300' width="612"/>
                    <Typography variant='h6' color="text.secondary">You have no notifications</Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={listContainerStyles}>
            {notifications.map((notification, index) => (
                <NotificationItem
                    key={notification._id}
                    notification={notification}
                    handleStatus={handleNotificationStatus}
                />
            ))}
            <div ref={sentinelRef} style={{ height: '1px' }} />
            {isFetchingNextPage && (
                <Box sx={bottomIndicatorStyles}>
                    <NotificationSkeleton />
                </Box>
            )}
            {!hasNextPage && notifications.length > 6 && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={endMessageStyles}
                >
                    You've reached the end
                </Typography>
            )}
        </Box>
    );
};

export default NotificationList;